import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  Container,
  Flex,
  Grid,
  Group,
  LoadingOverlay,
  NumberInput,
  Text,
  Divider,
} from "@mantine/core";
import { IconSettings, IconUsers, IconGift } from "@tabler/icons";
import AppBreadcrumb from "../../../components/AppBreadcrumb";
import { coupons_api } from "./coupons.service";
import { showSuccessToast, showErrorToast } from "utilities/Toast";

export default function CouponAdmin() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form State
  const [referralAmount, setReferralAmount] = useState(200);
  const [referralMinPurchase, setReferralMinPurchase] = useState(1000);
  const [referralInviteCount, setReferralInviteCount] = useState(4);
  const [welcomeAmount, setWelcomeAmount] = useState(100);
  const [welcomeMinPurchase, setWelcomeMinPurchase] = useState(500);

  // Load Settings
  const loadSettings = async () => {
    try {
      setLoading(true);
      const res = await coupons_api.settingsGet();
      if (res?.success && res?.data) {
        const data = res.data;
        setReferralAmount(data.referral_amount ?? 200);
        setReferralMinPurchase(data.referral_min_purchase ?? 1000);
        setReferralInviteCount(data.referral_invite_count ?? 4);
        setWelcomeAmount(data.welcome_amount ?? 100);
        setWelcomeMinPurchase(data.welcome_min_purchase ?? 500);
      } else {
        showErrorToast({
          title: "Error",
          message: res?.message || "Failed to load coupon configurations.",
        });
      }
    } catch (error) {
      showErrorToast({
        title: "Error",
        message: "An unexpected error occurred while loading settings.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  // Save Settings
  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        referral_amount: referralAmount,
        referral_min_purchase: referralMinPurchase,
        referral_invite_count: referralInviteCount,
        welcome_amount: welcomeAmount,
        welcome_min_purchase: welcomeMinPurchase,
      };
      const res = await coupons_api.settingsUpdate(payload);
      if (res?.success) {
        showSuccessToast({
          title: "Success",
          message: "Coupon settings updated successfully.",
        });
      } else {
        showErrorToast({
          title: "Error",
          message: res?.message || "Failed to update settings.",
        });
      }
    } catch (error) {
      showErrorToast({
        title: "Error",
        message: "An unexpected error occurred while saving settings.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container fluid style={{ position: "relative", minHeight: "80vh" }}>
      <LoadingOverlay visible={loading || saving} overlayBlur={2} />

      {/* HEADER SECTION */}
      <Box px={25} py={15}>
        <Flex justify="space-between" align="end">
          <Box mt={13}>
            <Text size="lg" fw={600} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <IconSettings size={22} color="#266f31" /> Coupon Admin
            </Text>
            <Box mt={6}>
              <AppBreadcrumb />
            </Box>
          </Box>
        </Flex>
      </Box>

      {/* CONFIGURATION CARDS */}
      <Box px={25} mt={12}>
        <Grid gutter="md">
          {/* Card 1: Referrer Settings */}
          <Grid.Col xs={12} md={6}>
            <Card shadow="sm" p="lg" radius="md" withBorder>
              <Group position="apart" mb="xs">
                <Text weight={600} size="md" color="#266f31" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <IconUsers size={20} /> Referrer Settings
                </Text>
              </Group>
              <Text size="xs" color="dimmed" mb="md">
                Configure the rewards given to the user who invites/refers new friends.
              </Text>
              <Divider mb="lg" />

              <Flex direction="column" gap="md">
                <NumberInput
                  label="Referrer Reward Amount (₹)"
                  placeholder="200"
                  value={referralAmount}
                  onChange={(val) => setReferralAmount(val || 0)}
                  min={0}
                  precision={2}
                  required
                />
                <NumberInput
                  label="Referrer Minimum Purchase (₹)"
                  placeholder="1000"
                  value={referralMinPurchase}
                  onChange={(val) => setReferralMinPurchase(val || 0)}
                  min={0}
                  precision={2}
                  required
                />
                <NumberInput
                  label="Required Invites"
                  placeholder="4"
                  value={referralInviteCount}
                  onChange={(val) => setReferralInviteCount(val || 0)}
                  min={1}
                  required
                />
              </Flex>
            </Card>
          </Grid.Col>

          {/* Card 2: Referee / Welcome Settings */}
          <Grid.Col xs={12} md={6}>
            <Card shadow="sm" p="lg" radius="md" withBorder>
              <Group position="apart" mb="xs">
                <Text weight={600} size="md" color="#266f31" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <IconGift size={20} /> Welcome (Referral) Settings
                </Text>
              </Group>
              <Text size="xs" color="dimmed" mb="md">
                Configure the welcome coupon given to the newly signed up referred user.
              </Text>
              <Divider mb="lg" />

              <Flex direction="column" gap="md">
                <NumberInput
                  label="Welcome Coupon Amount (₹)"
                  placeholder="100"
                  value={welcomeAmount}
                  onChange={(val) => setWelcomeAmount(val || 0)}
                  min={0}
                  precision={2}
                  required
                />
                <NumberInput
                  label="Welcome Minimum Purchase (₹)"
                  placeholder="500"
                  value={welcomeMinPurchase}
                  onChange={(val) => setWelcomeMinPurchase(val || 0)}
                  min={0}
                  precision={2}
                  required
                />
              </Flex>
            </Card>
          </Grid.Col>
        </Grid>

        {/* SAVE BUTTON */}
        <Flex justify="flex-end" mt="xl" mb="xl">
          <Button
            size="md"
            style={{ backgroundColor: "#266f31" }}
            onClick={handleSave}
            loading={saving}
          >
            Save Configurations
          </Button>
        </Flex>
      </Box>
    </Container>
  );
}
